import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { first, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { GlobalBlock } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { PicalunaSiteService } from '../../services/picaluna-site.service';
import { GlobalBlockUse, globalBlockUses, globalBlocksIn, newGlobalBlockId, sectionsOf } from './global-blocks';

/**
 * Website → Global blocks (#217): the blocks defined once and placed on pages by reference. Persists via
 * accountService.patchContentItem('globalBlocks', …); the per-row ☁ publishes just that global block, which changes
 * every page that places it. A global block a page still places can't be removed (the server refuses it too).
 */
@Component({
  selector: 'app-content-global-block-list',
  templateUrl: './content-global-block-list.component.html',
})
export class ContentGlobalBlockListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves: the table must not claim there are none while it loads. */
  accountLoading = true;
  loadFailed = false;
  publishingId: string | null = null;
  account$: Observable<Account>;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private modalService: ModalService,
    private picalunaSiteService: PicalunaSiteService
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: (err) => {
        this.accountLoading = false;
        this.loadFailed = true;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  blocks(content: any): GlobalBlock[] {
    return globalBlocksIn(content);
  }

  blockCount(block: GlobalBlock): number {
    return sectionsOf(block).length;
  }

  usedOn(content: any, block: GlobalBlock): GlobalBlockUse[] {
    return globalBlockUses(content, block._meta.id);
  }

  usedOnTitle(content: any, block: GlobalBlock): string {
    return this.usedOn(content, block)
      .map((u) => u.label)
      .join('\n');
  }

  onAdd(): void {
    this.open({ _meta: { id: newGlobalBlockId(), index: 0 }, name: '', sections: {} }, true);
  }

  onEdit(block: GlobalBlock): void {
    this.open(_.cloneDeep(block), false);
  }

  onDelete(content: any, block: GlobalBlock): void {
    if (this.usedOn(content, block).length) return;
    const modal = this.modalService.generic({
      title: 'Remove global block',
      copy: [`Remove "${block.name}"? No page uses it, so nothing on the site changes.`],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        this.accountService
          .removeContentItem('globalBlocks', block._meta.id)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }

  onPublish(content: any, block: GlobalBlock): void {
    if (this.publishingId) return;
    const uses = this.usedOn(content, block);
    const modal = this.modalService.generic({
      title: 'Publish this global block?',
      copy: [
        `This publishes "${block.name}" to the website preview.`,
        uses.length
          ? `It changes every page that uses it: ${uses.map((u) => u.label).join(', ')}. Nothing else on the site changes.`
          : 'No page uses it yet, so no page changes.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label: 'Publish this global block', key: 'publish', class: 'p-button-success' },
      ],
    });
    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') return;
        this.publishingId = block._meta.id;
        this.picalunaSiteService
          .publish(`globalBlocks:${block._meta.id}`)
          // take(1), not takeUntil: the dispatch must outlive the dialog (see the page list).
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingId = null;
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${block.name}" is being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when you are happy with the preview.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingId = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  private open(block: GlobalBlock, isNew: boolean): void {
    let account: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({ next: (a) => (account = a) });
    const content = account?.data?.content;
    this.modalService.editGlobalBlock({
      block,
      isNew,
      others: globalBlocksIn(content).filter((g) => g._meta.id !== block._meta.id),
      usedOn: globalBlockUses(content, block._meta.id),
      save: (g: GlobalBlock) => this.accountService.patchContentItem('globalBlocks', g).pipe(take(1)),
    });
  }
}
